/** True when the transcript has at least one non-empty assistant turn. */
export function hasAssistantTurns(transcript) {
    if (!Array.isArray(transcript))
        return false;
    // Guard `text`: assistantTranscript is a Prisma Json? read via a bare cast, so
    // a malformed entry can carry a non-string text — don't let `.length` throw.
    return transcript.some(turn => turn?.role === 'assistant' &&
        typeof turn.text === 'string' &&
        turn.text.length > 0);
}
/**
 * Maps an AssistantTranscript role to an OpenAI-compatible role.
 * Converts 'staff_member' to 'assistant' since OpenAI doesn't support custom roles.
 */
export function mapTranscriptRoleForOpenAI(role) {
    return role === 'staff_member' ? 'assistant' : role;
}
/**
 * Maps an AssistantTranscript role to an OpenAI chat completion message role.
 * Converts 'staff_member' and 'system' to 'assistant' for chat completion API.
 * Chat completion only supports 'user' and 'assistant' roles.
 */
export function mapTranscriptRoleToChatRole(role) {
    if (role === 'user')
        return 'user';
    // Map staff_member, assistant, and system all to 'assistant'
    return 'assistant';
}
/**
 * Gets a display name for a transcript role.
 * Maps roles to human-readable labels for UI display.
 */
export function getTranscriptRoleDisplayName(role) {
    switch (role) {
        case 'assistant':
            return 'Assistant';
        case 'user':
            return 'Caller';
        case 'system':
            return 'System';
        case 'staff_member':
            return 'Staff';
    }
}
/**
 * Maps transcript roles to prompt-friendly speaker labels.
 * Used when formatting transcripts for LLM prompts.
 */
export const TRANSCRIPT_ROLE_PROMPT_LABELS = {
    user: 'Caller',
    assistant: 'Agent',
    staff_member: 'Staff',
    system: 'System',
};
/**
 * Formats a transcript array into a labeled multi-line string for LLM prompts.
 * Filters out system turns and maps roles to human-readable speaker labels.
 */
export function buildTranscript(turns) {
    return turns
        .filter(t => t.role !== 'system')
        .map(t => `${TRANSCRIPT_ROLE_PROMPT_LABELS[t.role] ?? t.role}: ${t.text}`)
        .join('\n');
}
/**
 * Role styling configuration for UI components.
 * Maps roles to Tailwind CSS classes for consistent visual styling.
 */
export const TRANSCRIPT_ROLE_STYLES = {
    assistant: 'bg-blue-50 text-gray-700',
    user: 'bg-green-100 text-gray-700',
    system: 'bg-gray-100 text-gray-700',
    staff_member: 'bg-purple-100 text-gray-700',
};
/**
 * Gets Tailwind CSS classes for a transcript role badge.
 */
export function getTranscriptRoleStyles(role) {
    return TRANSCRIPT_ROLE_STYLES[role];
}
//# sourceMappingURL=transcript-utils.js.map